import sparkle from 'gif/sparkle.gif';
import star from 'gif/star.gif';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import { Button } from '../components/common/Button';
import LogClick from '../components/common/LogClick';
import { Layout } from '../components/Layout/Layout';
import { useNavigation } from '../hooks/useNavigation';
import { setFourCutImage } from '../store/photoSlice';
import { RootState } from '../store/rootReducer';
import { createFourCutImage } from '../utils/imageProcessing';
import { useAiImageAnalytics } from '../hooks/useAiImageAnalytics';
import {
  Grid,
  Container,
  FrameBox,
  BetaBox,
  Photo,
  LoadingTextBox,
  PhotoList,
  ChoosePhotoBox,
  ChooseFrameBox,
  PaginationControls,
  OriginalPhotoSection,
  AiPhotoSection,
} from './ChoosePhotoPage.styled.ts';
import { usePhotoSelection } from '../hooks/usePhotoSelection';
import { Beta } from '../components/common/Beta';
import { ArrowLeftIcon, ArrowRightIcon } from '../components/icons/Icons';

export function ChoosePhotoPage() {
  const { t } = useTranslation();
  const emotionTheme = useTheme();
  const { navigateToChooseFrame, navigateToPrint } = useNavigation();
  const dispatch = useDispatch();
  const frameRef = useRef<HTMLDivElement>(null);
  const { recordExitData } = useAiImageAnalytics();
  const {
    choosePhotos,
    selectedPhotos,
    aiProcessedPhotos,
    selectedFrameIndex,
    currentPage,
    totalPages,
    getCurrentPagePhotos,
    goToNextPage,
    goToPrevPage,
  } = usePhotoSelection();

  const block = useSelector((state: RootState) => state.block.block);
  const selectedThemeId = useSelector((state: RootState) => state.theme.selectedThemeId);

  const theme =
    selectedThemeId && block?.themes
      ? block.themes.find((t) => t.id === selectedThemeId) || block.themes[0]
      : block?.themes?.[0];
  const style = block?.style || {};
  const frames = theme?.frames || [];
  const frame = frames[selectedFrameIndex];

  const currentPagePhotos = getCurrentPagePhotos();

  return (
    <Layout>
      <Container>
        {/* 사진 선택 (좌측) */}
        <ChoosePhotoBox>
          <PaginationControls>
            <button onClick={goToPrevPage} disabled={currentPage === 0}>
              <ArrowLeftIcon disabled={currentPage === 0} theme={emotionTheme} size={30} />
            </button>
            <span>
              {currentPage + 1}/{totalPages}
            </span>
            <button onClick={goToNextPage} disabled={currentPage === totalPages - 1}>
              <ArrowRightIcon
                disabled={currentPage === totalPages - 1}
                theme={emotionTheme}
                size={30}
              />
            </button>
          </PaginationControls>

          {/* 원본 사진 (상단) */}
          <OriginalPhotoSection>
            <LoadingTextBox>
              <img src={sparkle} />
              <h4>{t('choosePhoto.originalImage')}</h4>
            </LoadingTextBox>
            <PhotoList>
              {currentPagePhotos.map(({ skinImprovement, isEmptySlot }, index) => {
                const actualIndex = currentPage * 4 + index;
                const selectedIndex = selectedPhotos.findIndex(
                  (photo) =>
                    photo && photo.photoIndex === actualIndex && photo.type === 'skinImprovement'
                );

                return (
                  <Photo
                    selected={selectedIndex !== -1}
                    isEmptySlot={isEmptySlot}
                    key={actualIndex}
                    onClick={() => {
                      if (!isEmptySlot && skinImprovement) {
                        choosePhotos(actualIndex, 'skinImprovement');
                      }
                    }}
                  >
                    {!isEmptySlot && <img src={skinImprovement || '/assets/gif/wait.gif'} />}
                    <div>
                      <div>{selectedIndex + 1}</div>
                    </div>
                  </Photo>
                );
              })}
            </PhotoList>
          </OriginalPhotoSection>

          {/* AI 사진 (하단) */}
          <AiPhotoSection>
            <div>
              <LoadingTextBox>
                <img src={star} />
                <h4>{t('choosePhoto.aiGeneratedImage')}</h4>
              </LoadingTextBox>
              <BetaBox>
                <Beta background={style[7]} color={style[5]} />
                <p>{t('choosePhoto.aiWarning')}</p>
              </BetaBox>
            </div>
            <PhotoList>
              {currentPagePhotos.map(({ characterize, isEmptySlot }, index) => {
                const actualIndex = currentPage * 4 + index;
                const selectedIndex = selectedPhotos.findIndex(
                  (photo) =>
                    photo && photo.photoIndex === actualIndex && photo.type === 'characterize'
                );

                return (
                  <Photo
                    selected={selectedIndex !== -1}
                    isEmptySlot={isEmptySlot}
                    key={actualIndex}
                    onClick={() => {
                      if (!isEmptySlot && characterize) {
                        choosePhotos(actualIndex, 'characterize');
                      }
                    }}
                  >
                    {!isEmptySlot && <img src={characterize || '/assets/gif/wait.gif'} />}
                    <div>
                      <div>{selectedIndex + 1}</div>
                    </div>
                  </Photo>
                );
              })}
            </PhotoList>
          </AiPhotoSection>
        </ChoosePhotoBox>

        {/* 사진 선택 결과 (우측) */}
        <ChooseFrameBox>
          <FrameBox ref={frameRef}>
            <img src={frame.real} />
            <Grid grid={frame.displayPhotoPositions}>
              {Array.from({ length: frame.displayPhotoPositions.length }, (_, index) => (
                <div key={index}>
                  <img
                    src={
                      selectedPhotos[index]
                        ? aiProcessedPhotos[selectedPhotos[index].photoIndex][
                            selectedPhotos[index].type
                          ]
                        : undefined
                    }
                  />
                </div>
              ))}
            </Grid>
          </FrameBox>
          <LogClick params={{ key: 'frame' }}>
            <Button
              onClick={() => {
                recordExitData().catch((err) => console.error('Failed to send analytics:', err));

                createFourCutImage(
                  selectedPhotos.map((sp) => {
                    return aiProcessedPhotos[sp.photoIndex][sp.type];
                  }),
                  frame.real,
                  frame.printPhotoPositions
                ).then((fourCutImage) => {
                  dispatch(setFourCutImage(fourCutImage));
                });
                
                navigateToPrint();
              }}
              disabled={
                !selectedPhotos.slice(0, frame.displayPhotoPositions.length).every((p) => p)
              }
            >
              {t('choosePhoto.complete')}
            </Button>
          </LogClick>
          <p onClick={navigateToChooseFrame}>{t('choosePhoto.reselectFrame')}</p>
        </ChooseFrameBox>
      </Container>
    </Layout>
  );
}
